import { SxProps, Theme } from "@mui/material";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greenMui,
  greyMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  redMui,
  rydeColors,
} from "@ryde/theme";

export const LiveMapPageShellStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "live-map-page-shell",
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  > * {
    flex-shrink: 0;
  }

  > .live-map-page-body {
    flex: 1;
    flex-shrink: 1;
    min-height: 0;
  }
`;

export const LiveMapPageBodyStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "live-map-page-body",
  className: "live-map-page-body",
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const LiveMapSplitterRootStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "live-map-splitter-root",
})<{ $isMobile?: boolean }>`
  min-width: 100%;
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: ${rydeColors.smoke};
  padding-top: 1px;
  padding-bottom: 1px;

  .grid_panel.mobile_full,
  .map_panel.mobile_full {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .grid_panel.mobile_full {
    background-color: ${greyMui._100};
    overflow: hidden;
  }

  .map_panel.mobile_full {
    background-color: ${rydeColors.smoke};
    padding: 3px;
  }

  .grid_panel.mobile_full.is-surface-hidden,
  .map_panel.mobile_full.is-surface-hidden {
    visibility: hidden;
    pointer-events: none;
    z-index: 0;
  }

  .grid_panel.mobile_full:not(.is-surface-hidden),
  .map_panel.mobile_full:not(.is-surface-hidden) {
    z-index: 1;
  }
`;

/** Fills vertical space so the rides grid splitter (grid + map) stretches inside the page. */
export const LiveMapGridMapShellStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "live-map-grid-map-shell",
})`
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const LiveMapTopWrapper = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "live-map-top-wrapper",
})`
  min-width: 100%;
  margin-bottom: 0;
  border-bottom: 1px solid ${greyMui._300};
  background-color: ${rydeColors.white};
  min-height: 30px;
  height: auto;
  padding-inline-start: 20px;
  padding-inline-end: 15px;
  padding-block: 6px;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  .right_stat_panel {
    margin-inline-start: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-wrap: nowrap;
    gap: 4px;
    padding-inline: 8px;
    .right_stat_panel {
      flex-shrink: 0;
      margin-inline-start: auto;
    }
  }
`;

/** Diagnostics + map polling controls share one visual group on the toolbar right. */
export const LiveMapDiagnosticsAndMapPollingStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "live-map-diagnostics-map-polling",
  },
)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-inline-end: 10px;
`;

const LIVE_MAP_DIAGNOSTICS_SWITCH_SCALE = 0.72;
const LIVE_MAP_DIAGNOSTICS_SWITCH_SCALE_COMPACT = 0.64;

export const LiveMapDiagnosticsPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "live-map-diagnostics-panel",
  },
)<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-inline-end: 0;
  opacity: 0.82;
  flex-shrink: 0;
  .diagnostics_label {
    font-size: ${({ $compact }) => ($compact ? "10px" : "11px")};
    font-weight: 400;
    color: ${blueGreyMui._500};
    white-space: nowrap;
    user-select: none;
  }
  .diagnostics_icon {
    color: ${blueGreyMui._500};
    flex-shrink: 0;
  }
  .MuiSwitch-root {
    transform: scale(
      ${({ $compact }) =>
        $compact
          ? LIVE_MAP_DIAGNOSTICS_SWITCH_SCALE_COMPACT
          : LIVE_MAP_DIAGNOSTICS_SWITCH_SCALE}
    );
    margin: ${({ $compact }) => ($compact ? "-8px -6px" : "-6px -4px")};
  }
`;

export const LiveMapPollingControlsGroupStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "live-map-polling-controls-group",
  },
)`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    gap: 2px;
  }
`;

export const LiveMapTopControlsSelectGroupStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "live-map-top-controls-select-group",
    className: "live-map-top-controls-select-group",
  },
)`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  .live-map-select-all {
    margin-inline-end: 0;
  }

  .live-map-selected-rows-toggle {
    margin-inline-start: 6px;
    flex-shrink: 0;

    .selected-rows-button {
      box-sizing: border-box;
      height: 32px;
      min-height: 32px;
      min-width: 52px;
      padding-block: 0;
      padding-inline: 6px;

      &.MuiButton-root {
        height: 32px;
        min-height: 32px;
      }
    }

    .selected-rows-icon {
      font-size: 18px;
    }
  }
`;

export const LiveMapTopControlsStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "live-map-top-controls",
})`
  min-width: 0;

  .status_synchro {
    display: flex;
    align-items: center;
    gap: 10px;
    &.map {
      padding-inline-start: 0;
    }
    .time_last {
      margin-inline-start: 20px;
      color: ${blueGreyMui._600};
    }
    .info_label {
      font-size: 12px;
      color: ${blueGreyMui._300};
    }
    .title_label {
      font-size: 12px;
      color: ${blueGreyMui._400};
    }
    .overtime_indicator {
      margin-inline-start: 12px;
      color: ${greenMui._500};
      &.warn {
        color: ${orangeMui._900};
      }
      &.error {
        color: ${redMui._900};
      }
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: 1;
    min-width: 0;

    .status_synchro {
      flex: 1;
      min-width: 0;
      overflow-x: auto;
      flex-wrap: nowrap;
      gap: 4px;
      -webkit-overflow-scrolling: touch;

      > .MuiIconButton-root {
        margin-inline: -2px;
      }
    }
  }
`;

export const TopRideInfoWrapper = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "ride-top-map-info-wrapper",
})`
  background-color: ${greyMui._200};
  border-radius: 12px;
  padding-block: 4px;
  padding-inline: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  label {
    font-size: 11px;
    color: ${greyMui._500};
  }
  span {
    font-size: 11px;
    color: ${blueGreyMui._500};
  }
`;

export const LiveMapPollInfoPillStyled = styled(TopRideInfoWrapper)`
  background-color: ${greyMui._100};
  cursor: default;

  &.live-map-poll-info--inline {
    background-color: transparent;
    border-radius: 0;
    padding-block: 0;
    padding-inline: 0;
    gap: 6px;
  }

  .live-map-pill-status-line {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 8px;
    row-gap: 2px;
    font-size: 11px;
    color: ${blueGreyMui._500};
  }

  .live-map-poll-info-icon {
    flex-shrink: 0;
    align-self: center;
    padding: 2px;
  }

  .live-map-poll-network-dot {
    display: inline-block;
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    align-self: center;
    /* Idle: no active polling — darker than pill background */
    background-color: ${greyMui._600};
    box-shadow: 0 0 0 1px ${greyMui._400};
  }

  .live-map-poll-network-dot.live-map-poll-network-dot--sync-active {
    background-color: ${greenMui._500};
    box-shadow: 0 0 0 1px ${greenMui._700};
  }

  /* Polling paused after at least one sync: neutral gray (not green). */
  .live-map-poll-network-dot.live-map-poll-network-dot--sync-paused {
    background-color: ${greyMui._500};
    box-shadow: 0 0 0 1px ${greyMui._400};
  }

  .live-map-poll-network-dot.live-map-poll-network-dot--in-flight {
    background-color: ${blueMui._600};
    box-shadow: 0 0 0 1px ${blueMui._800};
  }

  .live-map-pill-edit-ride-link {
    text-decoration: none;
    cursor: pointer;
    color: ${blueMui._600};
    &:hover {
      text-decoration: underline;
    }
  }

  .live-map-pill-extra-ids {
    font-size: 11px;
    color: ${blueGreyMui._500};
    user-select: none;
  }
`;

export const LiveMapPollTooltipLinesStyled = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "live-map-poll-tooltip-lines",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: start;
`;

export const LiveMapMapPollStatusLineStyled = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "live-map-map-poll-status-line",
  },
)`
  font-size: 11px;
  color: ${blueGreyMui._500};
`;

export const liveMapRefreshIntervalTimerIconSx: SxProps<Theme> = {
  fontSize: 18,
  color: greyMui._700,
};
